import React, { useEffect, useState } from "react";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import CustomSelect from "components/CustomSelect";
import GoalButton from "layouts/goals/components/GoalButton";
import goalSelectStyles from "layouts/goals/components/goalSelectStyles";
import FieldLabel from "./FieldLabel";
import {
  ASSETS_MESSAGES,
  DISPOSAL_REASONS,
  GIVEN_TO_PERSON_REASONS,
  personName,
  toAmountText,
} from "../assets.constants";

const todayStr = () => moment().format("YYYY-MM-DD");

// Takes an asset out of use for good: given as severance, sold, donated, lost,
// stolen, scrapped... If someone holds it, their assignment ends the same day.
function DisposeAssetModal({ open, onClose, onDispose, isSubmitting, asset }) {
  const assignment = asset?.current_assignment;
  const [reason, setReason] = useState("severance");
  const [to, setTo] = useState("");
  const [price, setPrice] = useState("");
  const [date, setDate] = useState(todayStr());
  const [notes, setNotes] = useState("");
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    setReason(assignment ? "severance" : "sold");
    setTo("");
    setPrice("");
    setDate(todayStr());
    setNotes("");
    setLocalError(null);
  }, [open, asset]);

  const givenToSomeone = GIVEN_TO_PERSON_REASONS.includes(reason);

  const handleDispose = async () => {
    if (assignment && date < assignment.assigned_at) {
      return setLocalError(ASSETS_MESSAGES.returnBeforeAssigned);
    }
    if (reason === "sold" && price !== "" && !(Number(price) >= 0)) {
      return setLocalError(ASSETS_MESSAGES.priceInvalid);
    }
    setLocalError(null);
    await onDispose(asset.id, {
      reason,
      date: date || todayStr(),
      disposed_to: givenToSomeone ? to : "",
      price: reason === "sold" ? price : undefined,
      notes,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={() => !isSubmitting && onClose()}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { overflow: "visible" } }}
    >
      <DialogTitle>Dispose of asset</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <SuiTypography variant="button" color="text" display="block" mb={2}>
          {asset?.name} will no longer count as a company asset. It stays in the records.
          {assignment && ` It is currently with ${personName(assignment.user)}.`} This can&apos;t be
          undone.
        </SuiTypography>
        {localError && (
          <SuiTypography variant="caption" color="error" display="block" mb={1}>
            {localError}
          </SuiTypography>
        )}
        <SuiBox mb={2}>
          <FieldLabel>Reason</FieldLabel>
          <CustomSelect
            options={DISPOSAL_REASONS}
            value={DISPOSAL_REASONS.find((option) => option.value === reason)}
            onChange={(option) => setReason(option.value)}
            customStyles={goalSelectStyles}
            menuPortalTarget={document.body}
          />
        </SuiBox>
        {givenToSomeone && (
          <SuiBox mb={2}>
            <FieldLabel>{reason === "sold" ? "Sold to" : "Given to"}</FieldLabel>
            <SuiInput
              autoComplete="off"
              placeholder={
                assignment ? `Leave blank for ${personName(assignment.user)}` : "Name (free text)"
              }
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </SuiBox>
        )}
        {reason === "sold" && (
          <SuiBox mb={2}>
            <FieldLabel>Sale price ({asset?.currency})</FieldLabel>
            <SuiInput
              inputProps={{ inputMode: "decimal" }}
              placeholder="Optional"
              value={price}
              onChange={(e) => setPrice(toAmountText(e.target.value))}
            />
          </SuiBox>
        )}
        <SuiBox mb={2}>
          <FieldLabel>Date</FieldLabel>
          <SuiInput type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </SuiBox>
        <FieldLabel>Notes</FieldLabel>
        <SuiInput
          multiline
          rows={2}
          placeholder="Optional, e.g. installments or what happened"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <GoalButton variant="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </GoalButton>
        <GoalButton variant="danger" icon="archive" loading={isSubmitting} onClick={handleDispose}>
          Dispose
        </GoalButton>
      </DialogActions>
    </Dialog>
  );
}

export default DisposeAssetModal;
